'use client'

import { useIntl } from 'react-intl'

import { usePrecisionNumberFormat } from '@b-stock/fe-core/bstock-react'
import { BuyingFormatEnum } from '@b-stock/search-api-client'

import {
  getPrimaryBuyingFormat,
  useDisplayBuyingFormats,
} from '@/buyer-portal/helpers/auctions/buyingFormat'
import { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'
import type { SearchResult } from '@/queries/listingSearchQuery'

/**
 * Renders the price line for a watchlist dropdown item.
 *
 * The dropdown lists every buying format, and each carries its price in a
 * different field and unit, so a single "dollars" assumption is wrong:
 * - Buy Now shows the live Buy Now price; Make an Offer the offering's start
 *   amount; auctions the current bid.
 * - Make an Offer PRICE amounts are stored in cents, so they scale down; every
 *   other amount is stored in dollars.
 * - Contract listings priced relative to COGS/MSRP render as a percentage with
 *   the matching " of COGS" / " of MSRP" suffix instead of a currency value.
 *
 * The format/precision decisions mirror the list-view price cell
 * (`CurrentPriceCellRenderer`) plus the offering cents handling from
 * `useFormatSpotBidAmount`.
 */
export const WatchlistItemPrice = ({ listing }: { listing: SearchResult }) => {
  const intl = useIntl()
  const combined = new CombinedListingSearchResult(listing)

  const buyingFormat = getPrimaryBuyingFormat(useDisplayBuyingFormats(listing))
  const { bidBasis, bidPrecision, currency, offering } = combined
  const isContract = combined.listingType === 'CONTRACT'
  const isMakeAnOffer = buyingFormat === BuyingFormatEnum.MakeAnOffer
  const offerIsPrice = offering?.offerAmountUnits === 'PRICE'

  // Contracts priced relative to COGS/MSRP, and percent-of-MSRP offers, render
  // as a percentage; everything else is a currency amount.
  const isPercent = bidBasis === 'RELATIVE' || (isMakeAnOffer && !offerIsPrice)

  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    currency,
    format: isPercent ? 'percent' : 'currency',
    precision: bidPrecision,
    // Make an Offer PRICE amounts are stored in cents (let the default cents
    // scaling apply); auction/Buy Now amounts and percentages are stored as-is.
    precisionStored: isMakeAnOffer && offerIsPrice ? undefined : 1,
  })

  const amount =
    buyingFormat === BuyingFormatEnum.BuyNow
      ? (combined.buyNowPrice ?? combined.currentPrice)
      : isMakeAnOffer
        ? (offering?.startOfferAmount ?? null)
        : combined.currentPrice

  if (!amount) return null

  const unitSuffix = isPercent
    ? intl.formatMessage({
        id: `Contracts.Enums.perUnit.${isContract ? (listing.bidUnits ?? 'MSRP') : 'MSRP'}`,
      })
    : ''

  return (
    <>
      {formatPrecisionNumber(amount)}
      {unitSuffix}
    </>
  )
}
